import events from "add-event-listener";
import * as React from "react";

import { IScrollToContext, scrollToContextTypes, ScrollToProvider } from "../../../Internals/Contexts/ScrollToContext";
import { LayoutEvents } from "../../../Internals/LayoutEvents/LayoutEvents";
import { emptyRef, pullContext } from "../../../TypingUtils";
import { scrollLeft, scrollRight } from "../scroll";
import { Arrows } from "../Arrows/Arrows";
import { HorizontalScroll } from "../HorizontalScroll/HorizontalScroll";

import { Root } from "./HorizontalScrollContainer.styles";

type StickyElementPosition = "Top" | "Bottom" | "Fixed";

interface HorizontalScrollContainerProps {
    children: JSX.Element;
    innerWidth: number;
    width: number;
    sidebarWidth: number;
    stopPoints: number[];
    scrollInner: null | HTMLElement;
    table: null | HTMLElement;
}

interface HorizontalScrollContainerState {
    scrollLeft: number;
    scrollBarPosition?: StickyElementPosition;
    arrowsPosition?: StickyElementPosition;
    showLeftArrow?: boolean;
    showRightArrow?: boolean;
    visibility?: boolean;
}

export class HorizontalScrollContainer extends React.PureComponent<
    HorizontalScrollContainerProps,
    HorizontalScrollContainerState
> {
    public static defaultProps = {
        stopPoints: [],
    };

    public static contextTypes = {
        ...scrollToContextTypes,
    };

    public state: HorizontalScrollContainerState = { scrollLeft: 0 };

    private scrollInner: null | HTMLElement = emptyRef;
    private readonly inner = React.createRef<HTMLDivElement>();

    public componentDidMount(): void {
        events.addEventListener(window, "scroll", this.handleRecalc);
        events.addEventListener(window, "resize", this.handleRecalc);
        events.addEventListener(window, "keydown", this.handleOnKeyDown);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: HorizontalScrollContainerProps): void {
        if (!this.scrollInner && nextProps.scrollInner) {
            this.scrollInner = nextProps.scrollInner;
            events.addEventListener(this.scrollInner, "scroll", this.handleRecalc);
        }
    }

    public componentDidUpdate(): void {
        this.handleRecalc();
    }

    public componentWillUnmount(): void {
        events.removeEventListener(window, "scroll", this.handleRecalc);
        events.removeEventListener(window, "resize", this.handleRecalc);
        events.removeEventListener(window, "keydown", this.handleOnKeyDown);

        if (this.scrollInner) {
            events.removeEventListener(this.scrollInner, "scroll", this.handleRecalc);
        }
    }

    public render(): JSX.Element {
        const children = React.cloneElement(this.props.children, { horizontalScroll: this.state.scrollLeft });

        return (
            <ScrollToProvider value={this.scrollTo}>
                <Root>
                    <Arrows
                        arrowsPosition={this.state.arrowsPosition ?? "Top"}
                        showLeftArrow={this.state.showLeftArrow}
                        showRightArrow={this.state.showRightArrow}
                        visible={this.state.visibility}
                        onLeftArrowClick={this.handleLeftArrowClick}
                        onRightArrowClick={this.handleRightArrowClick}
                    />
                    <HorizontalScroll
                        onChangeScrollLeft={this.handleChangeScrollLeft}
                        scrollBarPosition={this.state.scrollBarPosition}
                        innerWidth={(this.scrollInner?.scrollWidth ?? 0) + this.props.sidebarWidth}
                        scrollBarSize={this.props.width}
                        scrollPos={this.state.scrollLeft}
                        visibility={this.state.visibility}
                    />
                    <div ref={this.inner}>{children}</div>
                </Root>
            </ScrollToProvider>
        );
    }

    private readonly handleOnKeyDown = (event: KeyboardEvent) => {
        // 37 - LeftArrow, 39 -- rightArrow
        if (event.keyCode !== 37 && event.keyCode !== 39) {
            return;
        }
        if (document.activeElement == undefined || document.activeElement.nodeName.toLowerCase() === "input") {
            return;
        }
        if (this.inner.current == undefined) {
            throw new Error("InvalidProgramState");
        }

        const innerRect = this.inner.current.getBoundingClientRect();
        const screenHeight = document.documentElement.offsetHeight;
        if (this.scrollInner == undefined) {
            throw new Error("InvalidProgramState");
        }

        if (innerRect.top < screenHeight / 2 && innerRect.bottom > screenHeight / 2) {
            if (event.keyCode === 37) {
                scrollLeft(this.scrollInner, 300, this.props.stopPoints);
            } else {
                scrollRight(this.scrollInner, 300, this.props.stopPoints);
            }
        }
    };

    private readonly recalc = () => {
        const arrowState = this.getArrowState();
        const scrollState = this.getScrollState();
        if (this.scrollInner == undefined) {
            throw new Error("InvalidProgramState");
        }
        this.setState({
            scrollLeft: this.scrollInner.scrollLeft,
            ...arrowState,
            ...scrollState,
        });
    };

    private readonly getArrowState = (): {
        arrowsPosition: "Top" | "Bottom" | "Fixed";
        showLeftArrow: boolean;
        showRightArrow: boolean;
        visibility: boolean;
    } => {
        if (this.props.table == undefined) {
            throw new Error("InvalidProgramState");
        }
        const innerRect = this.props.table.getBoundingClientRect();
        const screenHeight = document.documentElement.offsetHeight;
        let arrowsPosition: StickyElementPosition = "Fixed";

        if (screenHeight / (innerRect.top + 50) < 2 && innerRect.top > 0) {
            arrowsPosition = "Top";
        }
        if (screenHeight / (innerRect.bottom - 50) > 2 || innerRect.bottom < 120) {
            arrowsPosition = "Bottom";
        }
        if (this.scrollInner == undefined) {
            throw new Error("InvalidProgramState");
        }

        return {
            arrowsPosition: arrowsPosition,
            showRightArrow: this.scrollInner.scrollLeft + this.props.innerWidth <= this.scrollInner.scrollWidth - 1,
            showLeftArrow: Boolean(this.scrollInner.scrollLeft),
            visibility: true,
        };
    };

    private readonly scrollTo = (component: Element) => {
        const { left, right } = component.getBoundingClientRect();
        const { clientWidth } = document.documentElement;
        if (this.scrollInner == undefined) {
            throw new Error("InvalidProgramState");
        }
        this.getContext().scrollTo?.(component);
        const { offsetWidth } = this.scrollInner;

        // 50 - отступ, 80 - ширина сайдбара
        if (right > clientWidth) {
            this.scrollInner.scrollLeft += right - clientWidth + 50;
        } else if (clientWidth - offsetWidth > left - 80) {
            this.scrollInner.scrollLeft -= clientWidth - offsetWidth - left - 50;
        }
    };

    private getScrollState(): { scrollBarPosition: StickyElementPosition; visibility: boolean } {
        const screenHeight = document.documentElement.offsetHeight;
        if (this.inner.current == undefined) {
            throw new Error("InvalidProgramState");
        }
        const innerRect = this.inner.current.getBoundingClientRect();

        let scrollBarPosition: StickyElementPosition = "Fixed";

        if (innerRect.top + 170 > screenHeight) {
            scrollBarPosition = "Top";
        }

        if (innerRect.bottom - screenHeight < 0) {
            scrollBarPosition = "Bottom";
        }

        return {
            scrollBarPosition: scrollBarPosition,
            visibility: true,
        };
    }

    private readonly handleRecalc = () => {
        if (!this.inner.current || !this.scrollInner) {
            return;
        }

        const rect = this.inner.current.getBoundingClientRect();
        const heightScreen = document.documentElement.offsetHeight;

        if (
            this.state.arrowsPosition !== "Fixed" &&
            this.state.scrollBarPosition !== "Fixed" &&
            (rect.top > heightScreen || rect.bottom < 0)
        ) {
            return;
        }

        if (this.props.innerWidth + 1 >= this.scrollInner.scrollWidth) {
            // if (this.state.scrollLeft !== 0 || this.state.visibility !== false)
            this.setState({
                scrollLeft: 0,
                visibility: false,
            });
        } else {
            this.recalc();
        }

        LayoutEvents.emit();
    };

    private readonly handleLeftArrowClick = () => {
        if (this.props.scrollInner == undefined) {
            throw new Error("InvalidProgramState");
        }
        scrollLeft(this.props.scrollInner, 300, this.props.stopPoints, this.props.width - this.props.sidebarWidth);
    };

    private readonly handleRightArrowClick = () => {
        if (this.props.scrollInner == undefined) {
            throw new Error("InvalidProgramState");
        }
        scrollRight(this.props.scrollInner, 300, this.props.stopPoints, this.props.width - this.props.sidebarWidth);
    };

    private readonly handleChangeScrollLeft = (nextScrollLeft: number) => {
        if (this.props.scrollInner != undefined) {
            this.props.scrollInner.scrollLeft = nextScrollLeft;
        }
    };

    public getContext(): IScrollToContext {
        return pullContext<IScrollToContext>(this);
    }
}
